import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, X, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";

// Job positions (not roles - roles are assigned by admin)
const JOB_POSITIONS = [
  "Driver",
  "Mechanic",
  "Dispatcher",
  "Yard Worker",
  "Office Staff",
  "Supervisor",
  "Other"
];

export const RequestAccessPage = ({ onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    pin: "",
    photo: null
  });
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [pinAvailable, setPinAvailable] = useState(null);
  const [checkingPin, setCheckingPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handlePinChange = async (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    
    const newPinDigits = [...pinDigits];
    newPinDigits[index] = value;
    setPinDigits(newPinDigits);
    
    const fullPin = newPinDigits.join("");
    setFormData(prev => ({ ...prev, pin: fullPin }));
    
    // Auto-focus next input
    if (value && index < 3) {
      document.getElementById(`req-pin-${index + 1}`)?.focus();
    }

    // Check PIN availability when all 4 digits entered
    if (fullPin.length === 4) {
      setCheckingPin(true);
      try {
        const response = await axios.get(`${API}/auth/check-pin/${fullPin}`);
        setPinAvailable(response.data.available);
      } catch (err) {
        setPinAvailable(false);
      } finally {
        setCheckingPin(false);
      }
    } else {
      setPinAvailable(null);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.phone || !formData.position || formData.pin.length !== 4) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!pinAvailable) {
      toast.error("Please choose a different PIN");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/access-requests`, formData);
      toast.success("Access request submitted! An admin will review your request.");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
              alt="Pacific Expressway" 
              className="h-16 w-auto mx-auto mb-3 object-contain"
            />
            <h1 className="text-xl font-bold text-slate-900">Pacific Expressway</h1>
            <p className="text-slate-500 text-sm">Fleet Maintenance</p>
            <p className="text-blue-600 font-medium mt-2">Request Access</p>
          </div>

          <div className="space-y-4">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First Name"
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last Name"
                  data-testid="input-last-name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="employee@pacificexpressway.com (optional)"
                data-testid="input-email"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                data-testid="input-phone"
              />
            </div>

            {/* Position - Simple dropdown, NOT role */}
            <div>
              <Label>Job Position *</Label>
              <Select 
                value={formData.position} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, position: value }))}
              >
                <SelectTrigger data-testid="select-position">
                  <SelectValue placeholder="Select your job position..." />
                </SelectTrigger>
                <SelectContent>
                  {JOB_POSITIONS.map((pos) => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">An admin will assign your access level after approval</p>
            </div>

            {/* PIN Selection */}
            <div>
              <Label>Choose a 4-digit PIN *</Label>
              <p className="text-xs text-slate-500 mb-2">This will be your login code</p>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  {pinDigits.map((digit, index) => (
                    <input
                      key={index}
                      id={`req-pin-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      className="w-12 h-12 text-center text-xl font-bold border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                      data-testid={`req-pin-input-${index}`}
                    />
                  ))}
                </div>
                {checkingPin && <span className="text-slate-500 text-sm">Checking...</span>}
                {!checkingPin && pinAvailable === true && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Available
                  </span>
                )}
                {!checkingPin && pinAvailable === false && (
                  <span className="text-red-500 text-sm flex items-center gap-1">
                    <XCircle className="w-4 h-4" /> Already in use
                  </span>
                )}
              </div>
            </div>

            {/* Photo (Optional) */}
            <div>
              <Label>Profile Photo (Optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Take a selfie for identification</p>
              <div className="flex items-center gap-4">
                {formData.photo ? (
                  <div className="relative">
                    <img src={formData.photo} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" />
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, photo: null }))}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-full flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Camera className="w-6 h-6 text-slate-400" />
                    <span className="text-xs text-slate-500 mt-1">Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handlePhotoCapture}
                      className="hidden"
                      data-testid="input-photo"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1">
              Back
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !pinAvailable}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="btn-submit-request"
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
